import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  Paper,
} from '@mui/material';
import {
  Storage,
  Security,
  Speed,
  Public,
  CheckCircle,
  ArrowForward,
  Router,
  CellTower,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const DataCenters = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    { icon: <Security />, title: 'Enterprise Security', desc: '24/7 surveillance, biometric access, and security guards' },
    { icon: <Speed />, title: 'High Performance', desc: '100Gbps connectivity with 99.999% uptime SLA' },
    { icon: <Public />, title: 'Global Reach', desc: 'Connected to major cloud providers and networks' },
    { icon: <Storage />, title: 'Scalable Solutions', desc: 'From single rack to full private suites' },
  ];

  return (
    <Box>
      <PageHeader 
        title="Data Centers" 
        subtitle="Secure, scalable, and highly available infrastructure"
      />

      {/* Features */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Data Center Solutions" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Enterprise-Grade Data Center Solutions
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  Our data centers are designed to meet the most demanding requirements 
                  for security, reliability, and performance.
                </Typography>
                <Stack spacing={2}>
                  {features.map((feature, index) => (
                    <Stack direction="row" spacing={2} alignItems="center" key={index}>
                      <Avatar sx={{ bgcolor: '#e6010815', color: '#e60108' }}>{feature.icon}</Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#191919' }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {feature.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/contact')}
                    sx={{ mt: 4, bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
                  >
                    Request a Quote
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop"
                  alt="Data Center"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Secure Your Infrastructure" 
        subtitle="Get premium data center space with carrier-neutral connectivity"
        buttonText="Explore Locations"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default DataCenters;